// Required-documents checklist (Increment F): shows which documents the tenant
// policy requires for this employee vs what is actually on file. Sits above
// EmployeeDocumentsCard on the Documents tab; `refreshKey` bumps after every
// upload/delete there so the checklist stays in sync.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Badge } from '@/features/shared/components/ui/badge';
import { ClipboardCheck, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import {
  getEmployeeDocumentStatus,
  type EmployeeDocumentStatus,
} from '@/services/documentRequirementsService';

export const RequiredDocumentsCard: React.FC<{ employeeId: string; refreshKey?: number }> = ({
  employeeId,
  refreshKey = 0,
}) => {
  const { t } = useTranslation('employees');
  const [status, setStatus] = useState<EmployeeDocumentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    // Only show the full-card spinner on first load; refetches after an
    // upload/delete just swap the data in place.
    if (!status) setLoading(true);
    getEmployeeDocumentStatus(employeeId)
      .then((s) => { if (active) { setStatus(s); setFailed(false); } })
      .catch(() => { if (active) setFailed(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, refreshKey]);

  // Quietly disappear if the endpoint isn't reachable — this is an assist
  // surface on top of the documents list, not a blocker.
  if (failed) return null;

  const required = status?.required || [];
  const missingTypes = new Set((status?.missing || []).map((m) => m.type));
  const onFile = required.length - required.filter((r) => missingTypes.has(r.type)).length;

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader>
        <CardTitle className="text-base text-orange-700 flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4" />
          {t('doc_requirements.card_title', { defaultValue: 'Required documents' })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading || !status ? (
          <div className="py-4 flex items-center text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            {t('doc_requirements.loading', { defaultValue: 'Checking required documents…' })}
          </div>
        ) : required.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('doc_requirements.none_required', { defaultValue: 'No documents are required for this employee.' })}
          </p>
        ) : (
          <>
            {/* Summary line */}
            <p className={`text-sm flex items-center gap-1.5 ${status.complete ? 'text-success' : 'text-foreground'}`}>
              {status.complete
                ? <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                : <AlertTriangle className="w-4 h-4 text-warning shrink-0" />}
              {t('doc_requirements.summary', {
                defaultValue: '{{onFile}} of {{total}} required documents on file',
                onFile,
                total: required.length,
              })}
            </p>

            <ul className="divide-y divide-border">
              {required.map((r) => {
                const missing = missingTypes.has(r.type);
                // Rich status from the compliance program, when the API sends it.
                const rich = ((status as any)?.requirements || []).find((x: any) => x.type === r.type);
                const richBadge: Record<string, [string, string]> = {
                  PENDING_VERIFICATION: ['bg-warning-soft text-warning-strong', 'Pending review'],
                  VERIFIED: ['bg-success-soft text-success-strong', 'Verified'],
                  REJECTED: ['bg-danger-soft text-danger-strong', 'Rejected'],
                  EXPIRING_SOON: ['bg-warning-soft text-warning-strong', 'Expires soon'],
                  EXPIRED: ['bg-danger-soft text-danger-strong', 'Expired'],
                };
                const rb = rich && !missing ? richBadge[rich.status] : undefined;
                return (
                  <li key={r.type} className="py-2.5">
                    <div className="flex items-center gap-3">
                      {missing ? (
                        <AlertTriangle className="w-4 h-4 text-warning shrink-0" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                      )}
                      <span className="text-sm text-foreground flex-1">{r.label}</span>
                      {missing ? (
                        <Badge className="border bg-warning-soft text-warning-strong border-transparent">
                          {t('doc_requirements.missing', { defaultValue: 'Missing' })}
                        </Badge>
                      ) : rb ? (
                        <Badge className={`border ${rb[0]} border-transparent`}>{rb[1]}</Badge>
                      ) : (
                        <Badge className="border bg-success-soft text-success-strong border-transparent">
                          {t('doc_requirements.on_file', { defaultValue: 'On file' })}
                        </Badge>
                      )}
                    </div>
                    {missing && (
                      <p className="text-xs text-muted-foreground mt-1 ml-7">
                        {t('doc_requirements.upload_hint', {
                          defaultValue: 'Upload it below with type {{label}}',
                          label: r.label,
                        })}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RequiredDocumentsCard;
